import { createPortal } from 'react-dom';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  type HTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { MOTION_DURATIONS } from './tokens.js';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

interface ModalStackEntry {
  id: symbol;
  dialogRef: RefObject<HTMLElement | null>;
  focusToRestoreRef: RefObject<HTMLElement | null | undefined>;
  layer: ModalLayer;
  order: number;
}

const modalStack: ModalStackEntry[] = [];
let modalMountOrder = 0;
let scrollLockCount = 0;
let bodyOverflowBeforeLock = '';

export type ModalLayer = 'planning' | 'choice' | 'viewer' | 'detail' | 'confirm';

const MODAL_LAYER_PRIORITY: Record<ModalLayer, number> = {
  planning: 115,
  choice: 120,
  viewer: 140,
  detail: 150,
  confirm: 160,
};

interface ModalDataAttributes {
  readonly [key: `data-${string}`]: string | number | undefined;
}

interface ModalBaseProps {
  children: ReactNode;
  onClose?: () => void;
  busy?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  backdropEvent?: 'click' | 'mouseDown';
  restoreFocus?: boolean;
  lockBodyScroll?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  returnFocusRef?: RefObject<HTMLElement | null>;
  /** Refocuses the first available control when a keyed wizard panel changes. */
  focusKey?: string | number;
  /**
   * Composite dialogs may keep their existing semantic root and register it
   * here; Modal still owns backdrop, focus, dismissal and scroll lifecycle.
   */
  externalDialogRef?: RefObject<HTMLElement | null>;
  portal?: boolean;
  layer: ModalLayer;
  as?: 'div' | 'section';
  role?: 'dialog' | 'alertdialog';
  describedBy?: string;
  overlayClassName?: string;
  className?: string;
  style?: CSSProperties;
  overlayTestId?: string;
  testId?: string;
  dialogData?: ModalDataAttributes;
}

type ModalAccessibleName =
  | { ariaLabel: string; labelledBy?: never }
  | { ariaLabel?: never; labelledBy: string };

export type ModalProps = ModalBaseProps & ModalAccessibleName;

export type ModalActionsProps = HTMLAttributes<HTMLDivElement>;

function joinedClassName(...classNames: readonly (string | undefined)[]): string {
  return classNames.filter(Boolean).join(' ');
}

function focusableElements(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)]
    .filter((element) =>
      !element.hidden
      && element.getAttribute('aria-hidden') !== 'true'
      && element.closest('[hidden], [aria-hidden="true"], [inert]') === null);
}

function isFocusableElement(element: HTMLElement): boolean {
  return element.matches(FOCUSABLE_SELECTOR)
    && !element.hidden
    && element.getAttribute('aria-hidden') !== 'true'
    && element.closest('[hidden], [aria-hidden="true"], [inert]') === null;
}

function lockScroll(): () => void {
  if (scrollLockCount === 0) {
    bodyOverflowBeforeLock = document.body.style.overflow;
  }
  scrollLockCount += 1;
  document.body.style.overflow = 'hidden';
  return () => {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
      document.body.style.overflow = bodyOverflowBeforeLock;
    }
  };
}

/**
 * Exit animation for every close path (Escape, backdrop, inner buttons,
 * programmatic state changes): the unmounting overlay is cloned into an inert
 * ghost that fades out and removes itself. Layout cleanup installs the clone
 * before the browser can paint the removed live overlay; a StrictMode remount
 * cancels that simulated exit synchronously.
 */
const MODAL_EXIT_MS = MOTION_DURATIONS.quick;

const EXIT_OVERLAY_OPACITY = '--cards-modal-exit-overlay-opacity';
const EXIT_DIALOG_OPACITY = '--cards-modal-exit-dialog-opacity';
const EXIT_DIALOG_TRANSFORM = '--cards-modal-exit-dialog-transform';
const EXIT_INHERITED_TEXT_PROPERTIES = [
  'color',
  'direction',
  'font-family',
  'font-size',
  'font-style',
  'font-variant',
  'font-weight',
  'letter-spacing',
  'line-height',
  'text-align',
  'text-transform',
  'word-spacing',
] as const;

function computedAnimationValue(
  element: HTMLElement,
  property: 'opacity' | 'transform',
  fallback: string,
): string {
  const value = window.getComputedStyle(element)[property];
  return value === '' ? fallback : value;
}

function preserveInheritedTextStyle(source: HTMLElement, target: HTMLElement): void {
  const computedStyle = window.getComputedStyle(source);
  for (const property of EXIT_INHERITED_TEXT_PROPERTIES) {
    const value = computedStyle.getPropertyValue(property);
    if (value !== '') target.style.setProperty(property, value);
  }
}

function createExitGhost(overlay: HTMLElement): HTMLElement | undefined {
  if (
    typeof window.matchMedia !== 'function'
    || window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) return undefined;
  const ghost = overlay.cloneNode(true) as HTMLElement;
  ghost.setAttribute('data-closing', 'true');
  ghost.setAttribute('aria-hidden', 'true');
  ghost.setAttribute('inert', '');
  // The ghost is presentation-only; it must never satisfy queries meant for
  // the live modal (tests, drivers) or intercept assistive tech.
  ghost.removeAttribute('role');
  for (const marked of ghost.querySelectorAll('[data-testid]')) marked.removeAttribute('data-testid');
  ghost.removeAttribute('data-testid');

  // cloneNode copies markup and inline styles, but not the current frame of a
  // running CSS animation or inherited styles from ancestors. Freeze both so
  // a close cannot jump in scale or typography when the ghost moves to body.
  preserveInheritedTextStyle(overlay, ghost);
  ghost.style.setProperty(
    EXIT_OVERLAY_OPACITY,
    computedAnimationValue(overlay, 'opacity', '1'),
  );
  const sourceDialog = overlay.querySelector<HTMLElement>(
    '.cards-kit-modal-dialog:not(.cards-card-detail-dialog)',
  );
  const ghostDialog = ghost.querySelector<HTMLElement>(
    '.cards-kit-modal-dialog:not(.cards-card-detail-dialog)',
  );
  if (sourceDialog !== null && ghostDialog !== null) {
    ghostDialog.style.setProperty(
      EXIT_DIALOG_OPACITY,
      computedAnimationValue(sourceDialog, 'opacity', '1'),
    );
    ghostDialog.style.setProperty(
      EXIT_DIALOG_TRANSFORM,
      computedAnimationValue(sourceDialog, 'transform', 'none'),
    );
  }
  return ghost;
}

function attachExitGhost(ghost: HTMLElement, onFinished: () => void): () => void {
  let finished = false;
  const finish = (): void => {
    if (finished) return;
    finished = true;
    window.clearTimeout(timer);
    ghost.remove();
    onFinished();
  };
  document.body.append(ghost);
  const timer = window.setTimeout(finish, MODAL_EXIT_MS + 60);
  return finish;
}

function topModal(): ModalStackEntry | undefined {
  let top: ModalStackEntry | undefined;
  for (const entry of modalStack) {
    if (
      top === undefined
      || MODAL_LAYER_PRIORITY[entry.layer] > MODAL_LAYER_PRIORITY[top.layer]
      || (
        MODAL_LAYER_PRIORITY[entry.layer] === MODAL_LAYER_PRIORITY[top.layer]
        && entry.order > top.order
      )
    ) {
      top = entry;
    }
  }
  return top;
}

function ModalRoot({
  children,
  onClose,
  busy = false,
  closeOnBackdrop = onClose !== undefined,
  closeOnEscape = onClose !== undefined,
  backdropEvent = 'click',
  restoreFocus = true,
  lockBodyScroll = true,
  initialFocusRef,
  returnFocusRef,
  focusKey,
  externalDialogRef,
  portal = false,
  layer,
  as = 'div',
  role = 'dialog',
  ariaLabel,
  labelledBy,
  describedBy,
  overlayClassName,
  className,
  style,
  overlayTestId,
  testId,
  dialogData,
}: ModalProps): ReactElement | null {
  const internalDialogRef = useRef<HTMLElement | null>(null);
  const dialogRef = externalDialogRef ?? internalDialogRef;
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const cancelExitGhostRef = useRef<(() => void) | undefined>(undefined);
  const modalIdRef = useRef(Symbol('cards-modal'));
  const originalFocusRef = useRef<HTMLElement | null | undefined>(undefined);
  const focusToRestoreRef = useRef<HTMLElement | null | undefined>(undefined);
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  const closeOnEscapeRef = useRef(closeOnEscape);
  const previousFocusKeyRef = useRef(focusKey);
  onCloseRef.current = onClose;
  busyRef.current = busy;
  closeOnEscapeRef.current = closeOnEscape;

  useLayoutEffect(() => {
    // A StrictMode remount cancels the simulated exit before the browser can
    // paint it, then acquires a fresh scroll lock for the live modal.
    cancelExitGhostRef.current?.();
    cancelExitGhostRef.current = undefined;
    const modalId = modalIdRef.current;
    if (originalFocusRef.current === undefined) {
      originalFocusRef.current = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    }
    if (focusToRestoreRef.current === undefined) {
      focusToRestoreRef.current = returnFocusRef?.current ?? originalFocusRef.current;
    }
    modalMountOrder += 1;
    modalStack.push({
      id: modalId,
      dialogRef,
      focusToRestoreRef,
      layer,
      order: modalMountOrder,
    });
    document.body.dataset.cardsModalOpen = 'true';
    const unlockScroll = lockBodyScroll ? lockScroll() : undefined;

    const dialog = dialogRef.current;
    const requestedInitialFocus = initialFocusRef?.current;
    const initialFocus = requestedInitialFocus !== undefined
      && requestedInitialFocus !== null
      && isFocusableElement(requestedInitialFocus)
      ? requestedInitialFocus
      : (dialog === null ? undefined : focusableElements(dialog)[0]) ?? dialog;
    if (topModal()?.id === modalId) initialFocus?.focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (topModal()?.id !== modalId) return;
      if (event.key === 'Escape') {
        if (busyRef.current) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (!closeOnEscapeRef.current || onCloseRef.current === undefined) return;
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || dialogRef.current === null) return;
      const focusable = focusableElements(dialogRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable.at(-1)!;
      if (!dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Capture the live overlay because React clears the ref during unmount.
    const overlayElement = overlayRef.current;
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const ghost = overlayElement === null ? undefined : createExitGhost(overlayElement);
      if (ghost === undefined) {
        unlockScroll?.();
      } else {
        // Layout-effect cleanup runs while the live overlay is still mounted.
        // Attach its visual successor synchronously, avoiding a paint with no
        // modal between the real node and the closing ghost. Keep scroll locked
        // until the ghost is gone so the viewport cannot reflow underneath it.
        let cancelExit = (): void => {};
        cancelExit = attachExitGhost(ghost, () => {
          unlockScroll?.();
          if (cancelExitGhostRef.current === cancelExit) {
            cancelExitGhostRef.current = undefined;
          }
        });
        cancelExitGhostRef.current = cancelExit;
      }
      const removedWasTop = topModal()?.id === modalId;
      let stackIndex = -1;
      for (let index = modalStack.length - 1; index >= 0; index -= 1) {
        if (modalStack[index]?.id === modalId) {
          stackIndex = index;
          break;
        }
      }
      if (stackIndex >= 0) modalStack.splice(stackIndex, 1);
      if (modalStack.length === 0) delete document.body.dataset.cardsModalOpen;
      const focusToRestore = focusToRestoreRef.current;
      const remainingTop = topModal();
      const remainingRestoreTarget = remainingTop?.focusToRestoreRef.current;
      if (
        remainingTop !== undefined
        && (
          remainingRestoreTarget === undefined
          || remainingRestoreTarget === null
          || !remainingRestoreTarget.isConnected
          || dialogRef.current?.contains(remainingRestoreTarget)
        )
      ) {
        remainingTop.focusToRestoreRef.current = focusToRestore;
      }
      queueMicrotask(() => {
        const topDialog = topModal()?.dialogRef.current;
        if (topDialog === undefined || topDialog === null) {
          if (restoreFocus && focusToRestore?.isConnected) focusToRestore.focus();
          return;
        }
        if (!removedWasTop) return;
        if (focusToRestore?.isConnected && topDialog.contains(focusToRestore)) {
          focusToRestore.focus();
          return;
        }
        (focusableElements(topDialog)[0] ?? topDialog).focus();
      });
    };
  }, []);

  useEffect(() => {
    if (previousFocusKeyRef.current === focusKey) return;
    previousFocusKeyRef.current = focusKey;
    if (topModal()?.id !== modalIdRef.current) return;
    const dialog = dialogRef.current;
    if (dialog === null) return;
    (focusableElements(dialog)[0] ?? dialog).focus();
  }, [focusKey]);

  if (typeof document === 'undefined') return null;

  const requestBackdropClose = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (
      event.target !== event.currentTarget
      || !closeOnBackdrop
      || busy
      || onClose === undefined
    ) return;
    onClose();
  };
  const dialog = externalDialogRef === undefined
    ? (() => {
        const dialogClassName = joinedClassName('cards-kit-modal-dialog', className);
        const dialogProps = {
          ...dialogData,
          className: dialogClassName,
          style,
          'data-testid': testId,
          role,
          'aria-modal': true,
          'aria-label': ariaLabel,
          'aria-labelledby': labelledBy,
          'aria-describedby': describedBy,
          'aria-busy': busy || undefined,
          tabIndex: -1,
        } as const;
        return as === 'section'
          ? <section {...dialogProps} ref={(node) => { dialogRef.current = node; }}>{children}</section>
          : <div {...dialogProps} ref={(node) => { dialogRef.current = node; }}>{children}</div>;
      })()
    : children;
  const overlay = (
    <div
      ref={overlayRef}
      className={joinedClassName('cards-kit-modal-overlay', overlayClassName)}
      data-modal-layer={layer}
      data-testid={overlayTestId}
      role="presentation"
      onClick={backdropEvent === 'click' ? requestBackdropClose : undefined}
      onMouseDown={backdropEvent === 'mouseDown' ? requestBackdropClose : undefined}
    >
      {dialog}
    </div>
  );
  return portal ? createPortal(overlay, document.body) : overlay;
}

export function ModalActions({
  className,
  ...props
}: ModalActionsProps): ReactElement {
  return (
    <div
      {...props}
      className={joinedClassName('cards-kit-modal-actions', className)}
    />
  );
}

export const Modal = Object.assign(ModalRoot, { Actions: ModalActions });
